import { Drawer, ScrollArea } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { lazy, Suspense, useMemo } from "react";
import useObjectDrawerStore, { type ObjectDrawerType } from "../../store/drawer/objectDrawerStore";
import DrawerSkeleton from "./DrawerSkeleton";

// Lazy-load renderers to keep bundle size down
const AccountDrawerContent = lazy(() => import("./renderers/AccountDrawerContent"));
const ContactDrawerContent = lazy(() => import("./renderers/ContactDrawerContent"));
const EmailDrawerContent = lazy(() => import("./renderers/EmailDrawerContent"));
const MeetingDrawerContent = lazy(() => import("./renderers/MeetingDrawerContent"));
const DocDrawerContent = lazy(() => import("./renderers/DocDrawerContent"));
const ConceptDrawerContent = lazy(() => import("./renderers/ConceptDrawerContent"));
const TaskDrawerContent = lazy(() => import("./renderers/TaskDrawerContent"));
const NoteDrawerContent = lazy(() => import("./renderers/NoteDrawerContent"));
const CustomObjectDrawerContent = lazy(() => import("./renderers/CustomObjectDrawerContent"));
const GenericObjectDrawerContent = lazy(() => import("./renderers/GenericObjectDrawerContent"));

/** Wider drawer for accounts/users since they have tabbed content */
function getSidebarCollapsedFromCookie(): boolean {
  if (typeof document === "undefined") return false;

  const match = document.cookie.match(/(?:^|;\s*)sidebar:state=([^;]+)/);
  return match?.[1] === "false";
}

function getDrawerSize(
  type: ObjectDrawerType | null,
  isDesktop: boolean,
  isSidebarCollapsed: boolean,
): string {
  if (type === "accounts" || type === "users") {
    if (!isDesktop) return "100vw";
    const sidebarWidth = isSidebarCollapsed ? "3rem" : "16rem";
    return `calc(100vw - ${sidebarWidth})`;
  }
  return "720px";
}

const UniversalObjectDrawer = () => {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const { isOpen, objectType, objectId, objectSlug, preloadedData, closeDrawer } =
    useObjectDrawerStore();
  const isSidebarCollapsed = getSidebarCollapsedFromCookie();

  const content = useMemo(() => {
    if (!objectType || !objectId) return null;

    const commonProps = { objectId, preloadedData };

    switch (objectType) {
      case "accounts":
        return <AccountDrawerContent {...commonProps} />;
      case "users":
        return <ContactDrawerContent {...commonProps} />;
      case "email":
        return <EmailDrawerContent {...commonProps} />;
      case "meeting":
        return <MeetingDrawerContent {...commonProps} />;
      case "doc":
        return <DocDrawerContent {...commonProps} />;
      case "concept":
        return <ConceptDrawerContent {...commonProps} />;
      case "task":
        return <TaskDrawerContent {...commonProps} />;
      case "note":
        return <NoteDrawerContent {...commonProps} />;
      case "custom-object":
        return <CustomObjectDrawerContent {...commonProps} objectSlug={objectSlug} />;
      default:
        return (
          <GenericObjectDrawerContent
            objectId={objectId}
            objectType={objectType}
            preloadedData={preloadedData}
          />
        );
    }
  }, [objectType, objectId, objectSlug, preloadedData]);

  return (
    <Drawer
      opened={isOpen}
      onClose={closeDrawer}
      position="right"
      size={getDrawerSize(objectType, !!isDesktop, isSidebarCollapsed)}
      withCloseButton={false}
      withOverlay
      overlayProps={{ blur: 4, opacity: 0.15 }}
      // Sit above shadcn/Radix sheets (z-201 — HealthDetailSheet, ObjectDetailSheet,
      // TaskDetailSheet) so an entity drawer opened from a doc inside one of those
      // sheets is visible, not hidden behind it. Stays below in-drawer Mantine menus
      // (z-300) and filter popovers (z-400).
      zIndex={250}
      transitionProps={{ duration: 300 }}
      classNames={{
        body: "p-0 h-full",
        content: "flex flex-col",
      }}
      styles={{
        content: { maxWidth: "100vw" },
      }}
    >
      <Suspense fallback={<DrawerSkeleton />}>
        {content}
      </Suspense>
    </Drawer>
  );
};

export default UniversalObjectDrawer;
